import { CheckCircle, Target, Users, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { teamMembers, brands } from '../mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-yellow-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            About Unite Marketing
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            We are a marketing agency on a mission to Unite with businesses to help them be <span className="font-semibold text-gray-900">consistent, strategic, and innovative</span> with their digital and content marketing.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#FBD116]/10 border-l-4 border-[#FBD116] p-8 rounded-r-2xl">
            <p className="text-2xl text-gray-900 font-medium italic">
              "We Unite as one of your staff, at a fraction of the cost, with strategic capability."
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                We're Part of Your Team
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our team is efficient, knowledgeable, and diverse in their skillset. They have worked with large corporates to start-ups.
                </p>
                <p>
                  Brands they have worked for include <span className="font-semibold text-gray-900">Xero, Microsoft, Dicker Data, TradeMe, Pushpay, FNZ</span> and several accounting firms.
                </p>
                <p>
                  We have specifically done marketing for Managed Service Providers (MSPs) or IT B2B/Tech companies for the last 7 years.
                </p>
              </div>
              <div className="mt-8">
                <Button
                  asChild
                  className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold"
                >
                  <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
                    Let's Talk
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What We Stand For
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: CheckCircle,
                title: 'Honesty & Integrity',
                description: 'Being straight up, honest and respectful in all our dealings.'
              },
              {
                icon: Target,
                title: 'Strategic Excellence',
                description: 'We get things done efficiently, and strategically with proven results.'
              },
              {
                icon: Users,
                title: 'True Partnership',
                description: 'We Unite with you as part of your team, not just another vendor.'
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#FBD116] transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#FBD116]/10 flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-gray-900" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Our Areas of Expertise
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Marketing for MSPs & Tech Companies',
                description: 'We have specifically done marketing for Managed Service Providers (MSPs) or IT B2B/Tech companies for the last 7 years. This is why we started Unite Nation for MSPs needing marketing & collaborative support.',
                icon: '💻'
              },
              {
                title: 'Marketing Support Made Easy',
                description: 'Our specialty is getting the basics done, and marketing projects that grow your business. By investing minimal time, you can achieve consistency with newsletters, social media, SEO and website updates.',
                icon: '✅'
              },
              {
                title: 'Marketing & Sales Support',
                description: 'Our staff are not only marketers. We have all been in sales as well. This means that you will have someone on your team that understands the full sales process and can chip in where you need help.',
                icon: '📈'
              },
              {
                title: 'Marketing for Accounting Firms',
                description: 'Our staff has a background working with Accounting firms. We have completed several successful marketing strategies for both MSPs and Accounting firms with proven results.',
                icon: '📊'
              }
            ].map((item, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            Trusted by Leading Brands
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {brands.map((brand, index) => (
              <div key={index} className="text-gray-400 font-bold text-xl">
                {brand.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book a no-obligation chat or free marketing audit.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold text-lg px-8"
          >
            <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
              Book Your Free Call
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
